import { useAuth } from '@/app/AuthContext.jsx';
import {
  useBankAccounts,
  useBillingProfiles,
  useCan,
  useExpFeatures,
  usePricingTiers,
  usePricingUsage,
} from '@/hooks';
import {
  firstBillingProfile,
  formatCLP,
  formatDisplay as formatPhone,
} from '@/lib';
import { qk } from '@/lib/queryKeys';
import { deleteBankAccount } from '@/lib/recepthor-api';
import { BancoTab } from '@/pages/configuracion/ConfiguracionForm.jsx';
import {
  Button,
  CfgFieldDisplay as FieldDisplay,
  HelperText,
  Modal,
  PageHead,
} from '@/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import ComprobantesPago from './ComprobantesPago.jsx';
import EditarCuentaBancariaModal from './EditarCuentaBancariaModal.jsx';
import EditarFacturacionModal from './EditarFacturacionModal.jsx';
import './Facturacion.css';

// Normalises the current billing profile into raw, edit-ready values ('' when
// missing) and surfaces `billingProfileId` so the edit modal can create
// (null → POST) vs update (set → PUT). Read views apply their own '—' fallback.
function mapBillingProfile(data) {
  const p = firstBillingProfile(data) ?? {};
  return {
    billingProfileId: p.billingProfileId ?? null,
    rut: p.rut ?? '',
    legalName: p.legalName ?? '',
    businessActivity: p.businessActivity ?? '',
    address: p.address ?? '',
    city: p.city ?? '',
    commune: p.commune ?? '',
    email: p.email ?? '',
    phone: p.phone ?? '',
    actecoCode: p.actecoCode ?? '',
  };
}

function mapBankAccounts(data) {
  const results = Array.isArray(data) ? data : (data?.results ?? []);
  return results.map((a) => ({
    id: a.bankAccountId,
    bankAccountId: a.bankAccountId,
    bankCode: a.bankCode ?? '',
    accountType: a.accountType ?? '',
    holderName: a.holderName ?? '',
    accountNumber: a.accountNumber ?? '',
    rut: a.rut ?? '',
    isDefault: Boolean(a.isDefault),
  }));
}

// ── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, note }) {
  return (
    <div className="fac-kpi">
      <div className="fac-kpi__label">{label}</div>
      <div className="fac-kpi__value">{value}</div>
      {note && <div className="fac-kpi__note">{note}</div>}
    </div>
  );
}

// ── Tarifa tab ────────────────────────────────────────────────────────────────
// Table and tramo are live data from the charge side: the canonical tier
// table comes from GET /pricing/tiers and the vigente tramo from
// GET /pricing/usage (the tier the NEXT notification pays, counted over the
// Chile calendar year), so this tab can never disagree with the fee charged.
// While loading — or if either endpoint errors — the KPIs show an explicit
// '—' and no tramo is highlighted; never a fake number.

// "1 – 200" for a bounded tier; the open-ended last tier travels max: null.
function tierRange(tier) {
  return tier.max == null ? `${tier.min} +` : `${tier.min} – ${tier.max}`;
}

function TarifaTab() {
  const { data: table, isError: tiersError } = usePricingTiers();
  const { data: usage, isError: usageError } = usePricingUsage();
  // Gate on the error flags too: after a failed background refetch React
  // Query keeps the last successful data, and a stale count/tramo rendered as
  // vigente would contradict the honest-placeholder contract above.
  const isError = tiersError || usageError;
  const tiers = !isError && Array.isArray(table?.tiers) ? table.tiers : [];
  const count =
    !isError && typeof usage?.notificationCount === 'number'
      ? usage.notificationCount
      : null;
  const tramo =
    !isError && usage?.currentTier != null
      ? (tiers.find((t) => t.tier === usage.currentTier) ?? null)
      : null;

  return (
    <div className="fac-tarifa">
      <div className="fac-kpi-row">
        <KpiCard
          label="Tramo vigente"
          value={tramo ? `Tramo ${tramo.tier}` : '—'}
          note={tramo ? `${tierRange(tramo)} dilig. / año` : undefined}
        />
        <KpiCard
          label="Fee por diligencia"
          value={tramo ? formatCLP(tramo.netPrice) : '—'}
          note={tramo ? `+ IVA ${formatCLP(tramo.ivaAmount)}` : undefined}
        />
        <KpiCard
          label="Diligencias del año"
          value={count ?? '—'}
          note={isError ? 'No se pudo cargar' : 'determinan su tramo'}
        />
      </div>

      <div className="fac-card">
        <div className="fac-card__head">
          <span className="fac-card__title">Tramos de fee</span>
          <span className="fac-card__hint">
            Se recalcula con su volumen anual
          </span>
        </div>
        <div className="fac-table-wrap">
          <table className="fac-table">
            <thead>
              <tr>
                <th>Tramo</th>
                <th>Diligencias / año</th>
                <th className="fac-table__num">Fee por diligencia</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => {
                const isActive = tramo != null && t.tier === tramo.tier;
                return (
                  <tr key={t.tier} className={isActive ? 'row--on' : ''}>
                    <td>
                      {`Tramo ${t.tier}`}
                      {isActive && <span className="tag tag--ok">Actual</span>}
                    </td>
                    <td>{tierRange(t)}</td>
                    <td className="fac-table__num mono">
                      {formatCLP(t.netPrice)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="fac-card__foot">
          El comprobante mensual de Recepthor cobra solo el fee + IVA. Los
          honorarios van por Pagos, con boleta del receptor.
        </p>
      </div>
    </div>
  );
}

// ── Comprobantes tab ──────────────────────────────────────────────────────────
// Recibos de pago del abogado. No son DTE del SII.

function ComprobantesTab() {
  return (
    <div className="fac-comprobantes">
      <ComprobantesPago />
    </div>
  );
}

// ── Datos tributarios tab ─────────────────────────────────────────────────────
// Reuses the shared CfgFieldDisplay (imported as FieldDisplay from @/ui).

function DatosTributariosTab({ datosFacturacion, failed, onEditar }) {
  const d = datosFacturacion ?? {};
  return (
    <div className="fac-card fac-datos">
      <div className="fac-card__head">
        <span className="fac-card__title">Datos de facturación</span>
        <span className="fac-card__hint">
          {failed ? 'No se pudieron cargar' : 'Aparecen en sus comprobantes'}
        </span>
        {onEditar && !failed && (
          <button
            type="button"
            className="t-btn t-btn--quiet t-btn--sm fac-datos__edit"
            onClick={() => onEditar('facturacion')}
          >
            Editar
          </button>
        )}
      </div>
      <div className="fac-datos__grid">
        <FieldDisplay label="Razón social" value={d.legalName || '—'} mono />
        <FieldDisplay label="RUT" value={d.rut || '—'} mono />
        <FieldDisplay
          label="Actividad"
          value={
            d.actecoCode
              ? `${d.actecoCode} · ${d.businessActivity}`
              : d.businessActivity || '—'
          }
          mono
          fullWidth
          ellipsis
        />
        <FieldDisplay
          label="Dirección"
          value={
            [d.address, d.city, d.commune].filter(Boolean).join(', ') || '—'
          }
          mono
          fullWidth
          ellipsis
        />
        <FieldDisplay label="Email" value={d.email || '—'} mono />
        <FieldDisplay
          label="Teléfono"
          value={formatPhone(d.phone) || '—'}
          mono
        />
      </div>
    </div>
  );
}

// ── Main Facturacion page ─────────────────────────────────────────────────────

const TABS = [
  { key: 'tarifa', label: 'Tarifa' },
  { key: 'datos', label: 'Datos tributarios' },
  { key: 'cuentas-bancarias', label: 'Cuentas bancarias', experimental: true },
  { key: 'comprobantes', label: 'Comprobantes' },
];

export default function Facturacion() {
  const canRead = useCan('billing:read');
  const { organizationId } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  // Cuentas bancarias is an experimental tab (internal-only via the flag).
  const [exp] = useExpFeatures();
  const [bankEditing, setBankEditing] = useState(null);

  // The active tab lives in the URL (/facturacion/:tab) so it is linkable and
  // survives a refresh (REC-1108). An unknown, absent, or gated-off tab falls
  // back to Tarifa.
  const { tab } = useParams();
  const navigate = useNavigate();
  const availableTabs = TABS.filter((t) => !t.experimental || exp);
  const activeTab = availableTabs.some((t) => t.key === tab) ? tab : 'tarifa';
  // activeTab is always a member of availableTabs (the fallback 'tarifa' is
  // never experimental), so the lookup can't miss — no defensive fallback.
  const activeTabLabel = availableTabs.find((t) => t.key === activeTab).label;

  // Wire the topbar breadcrumb (Finanzas > Facturación > {tab}) and hide the
  // default org head — this page was the only layout view that never set it, so
  // the topbar showed the org/user name instead (REC-1109). Mirrors the
  // Configuracion pattern; the leaf updates as the active tab changes.
  const { setBreadcrumb, setPills, setHideOrgCrumb } = useOutletContext();
  useEffect(() => {
    // Skip while unauthorized: the page redirects below (!canRead), so setting
    // the crumb here would flash "Facturación" in the topbar for a view that
    // never renders.
    if (!canRead) return;
    setHideOrgCrumb?.(true);
    setBreadcrumb([
      'Finanzas',
      // Land on the concrete default tab, not the bare /facturacion redirect
      // route — navigating there unmounts + remounts the page (and refetches).
      { label: 'Facturación', onClick: () => navigate('/facturacion/tarifa') },
      activeTabLabel,
    ]);
    setPills([]);
    return () => {
      setHideOrgCrumb?.(false);
      setBreadcrumb([]);
      setPills([]);
    };
  }, [
    setBreadcrumb,
    setPills,
    setHideOrgCrumb,
    navigate,
    activeTabLabel,
    canRead,
  ]);

  // Una lectura caída dejaba todos los campos en guion, que es lo mismo que
  // rinde una organización que todavía no los llenó. Y sobre esa pantalla el
  // botón «Editar» abre el formulario con lo leído: editar lo que no se pudo
  // leer es la vía por la que se guardan datos en blanco sobre datos reales.
  const { data: billingProfilesData, isError: billingFailed } =
    useBillingProfiles(organizationId);

  const { data: bankAccountsData, isError: bankAccountsFailed } =
    useBankAccounts(organizationId);

  const datosFacturacion = useMemo(
    () => mapBillingProfile(billingProfilesData ?? []),
    [billingProfilesData],
  );

  const cuentasBancarias = useMemo(
    () => mapBankAccounts(bankAccountsData),
    [bankAccountsData],
  );

  const invalidateBankAccounts = () =>
    queryClient.invalidateQueries({ queryKey: qk.bankAccounts.all() });

  if (!canRead) {
    return <Navigate to="/sistema/organizacion" replace />;
  }

  function handleEditar(section) {
    if (section === 'facturacion') {
      setEditing('facturacion');
    }
  }

  function handleAgregarBanco() {
    setBankEditing({ kind: 'banco-add' });
  }

  function handleEditarBanco(cuenta) {
    setBankEditing({ kind: 'banco-edit', cuenta });
  }

  function handleEliminarBanco(cuenta) {
    setBankEditing({ kind: 'banco-delete', cuenta });
  }

  return (
    <>
      <div className="page reveal">
        <PageHead
          kicker="Finanzas"
          title="Facturación"
          sub="Recepthor cobra un fee por diligencia transada. El fee baja por tramos según su volumen anual de diligencias pagadas. Los honorarios del receptor se pagan aparte, en Pagos."
        />

        <div className="tabs" role="tablist">
          {availableTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`tab${activeTab === t.key ? ' tab--on' : ''}`}
              onClick={() => navigate(`/facturacion/${t.key}`)}
            >
              {t.label}
              {t.experimental && (
                <span className="soon-badge" aria-hidden="true">
                  Próx
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="fac-tab-panel">
          {activeTab === 'tarifa' && <TarifaTab />}
          {activeTab === 'datos' && (
            <DatosTributariosTab
              datosFacturacion={datosFacturacion}
              failed={billingFailed}
              onEditar={handleEditar}
            />
          )}
          {activeTab === 'cuentas-bancarias' && exp && (
            <BancoTab
              cuentas={cuentasBancarias}
              failed={bankAccountsFailed}
              onAgregar={handleAgregarBanco}
              onEditar={handleEditarBanco}
              onEliminar={handleEliminarBanco}
            />
          )}
          {activeTab === 'comprobantes' && <ComprobantesTab />}
        </div>
      </div>
      {editing === 'facturacion' && (
        <EditarFacturacionModal
          profile={datosFacturacion}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: qk.billingProfiles.all(),
            });
            setEditing(null);
          }}
        />
      )}
      {bankEditing &&
        (bankEditing.kind === 'banco-add' ||
          bankEditing.kind === 'banco-edit') && (
          <EditarCuentaBancariaModal
            account={bankEditing.cuenta ?? null}
            onClose={() => setBankEditing(null)}
            onSaved={async () => {
              await invalidateBankAccounts();
              setBankEditing(null);
            }}
          />
        )}
      {bankEditing && bankEditing.kind === 'banco-delete' && (
        <EliminarCuentaBancariaModal
          cuenta={bankEditing.cuenta}
          onClose={() => setBankEditing(null)}
          onDeleted={async () => {
            await invalidateBankAccounts();
            setBankEditing(null);
          }}
        />
      )}
    </>
  );
}

// Two-step delete confirmation for a bank account.
function EliminarCuentaBancariaModal({ cuenta, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isDefault = Boolean(cuenta?.isDefault);

  async function handleDelete() {
    setError('');
    setLoading(true);
    try {
      await deleteBankAccount(cuenta.bankAccountId);
      await onDeleted?.();
    } catch (err) {
      const msg = err?.message ?? '';
      setError(
        /default/i.test(msg)
          ? 'No puedes eliminar la cuenta principal. Marca otra como principal primero.'
          : 'No se pudo eliminar. Intenta nuevamente.',
      );
      setLoading(false);
    }
  }

  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header title="Eliminar cuenta bancaria" onClose={onClose} />
      <Modal.Body>
        {isDefault ? (
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            <b>{cuenta?.holderName}</b> es tu cuenta principal y no puede
            eliminarse. Marca otra cuenta como principal y vuelve a intentarlo.
          </p>
        ) : (
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Vas a eliminar la cuenta de <b>{cuenta?.holderName}</b>. Esta acción
            no se puede deshacer.
          </p>
        )}
        {error && <HelperText kind="err">{error}</HelperText>}
      </Modal.Body>
      <Modal.Foot>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={loading}
        >
          {isDefault ? 'Entendido' : 'Cancelar'}
        </Button>
        {!isDefault && (
          <Button
            variant="danger-solid"
            type="button"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Eliminando…' : 'Eliminar cuenta'}
          </Button>
        )}
      </Modal.Foot>
    </Modal>
  );
}
